'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import DashboardStats, { StatItem } from './DashboardStats';
import CaseTableFilters, { FilterState } from './CaseTableFilters';
import CaseTable, { CaseRecord } from './CaseTable';
import ActivityFeed, { ActivityItem } from './ActivityFeed';
import Header from '@/components/common/Header';
import RoleBasedNavigation from '@/components/common/RoleBasedNavigation';
import RouteGuard from '@/components/common/RouteGuard';
import { useAuth } from '@/context/AuthContext';



const MOCK_CASES: CaseRecord[] = [
  { id: '1', caseRef: 'QRSL-2026-0041', clientName: 'Margaret O\'Sullivan', clientEmail: 'margaret.osullivan@email.ie', appType: 'ARF', submissionDate: '28/02/2026', lastActivity: '2026-03-04', lastActivityLabel: '2 hours ago', status: 'UNDER_REVIEW', hasNewActivity: true, documentsCount: 4 },
  { id: '2', caseRef: 'QRSL-2026-0038', clientName: 'Patrick Brennan', clientEmail: 'p.brennan@techcorp.ie', appType: 'PRB', submissionDate: '25/02/2026', lastActivity: '2026-03-03', lastActivityLabel: '1 day ago', status: 'MORE_INFO_REQUIRED', hasNewActivity: true, documentsCount: 2 },
  { id: '3', caseRef: 'QRSL-2026-0035', clientName: 'Siobhán Murphy', clientEmail: 'siobhan.murphy@gmail.com', appType: 'PRSA', submissionDate: '20/02/2026', lastActivity: '2026-03-01', lastActivityLabel: '3 days ago', status: 'APPLICATION_SUBMITTED', hasNewActivity: false, documentsCount: 3 },
  { id: '4', caseRef: 'QRSL-2026-0031', clientName: 'Declan Fitzgerald', clientEmail: 'declan.fitz@lawfirm.ie', appType: 'ARF', submissionDate: '15/02/2026', lastActivity: '2026-02-28', lastActivityLabel: '5 days ago', status: 'APPROVED', hasNewActivity: false, documentsCount: 6 },
  { id: '5', caseRef: 'QRSL-2026-0028', clientName: 'Aoife Kelly', clientEmail: 'aoife.kelly@medclinic.ie', appType: 'PRSA', submissionDate: '10/02/2026', lastActivity: '2026-02-25', lastActivityLabel: '8 days ago', status: 'APPROVED', hasNewActivity: false, documentsCount: 5 },
  { id: '6', caseRef: 'QRSL-2026-0024', clientName: 'Ciarán Walsh', clientEmail: 'ciaran.walsh@constructions.ie', appType: 'PRB', submissionDate: '05/02/2026', lastActivity: '2026-02-20', lastActivityLabel: '13 days ago', status: 'DECLINED', hasNewActivity: false, documentsCount: 3 },
  { id: '7', caseRef: 'QRSL-2026-0019', clientName: 'Niamh Doyle', clientEmail: 'niamh.doyle@retail.ie', appType: 'ARF', submissionDate: '28/01/2026', lastActivity: '2026-02-10', lastActivityLabel: '23 days ago', status: 'CANCELLED', hasNewActivity: false, documentsCount: 1 },
  { id: '8', caseRef: 'QRSL-2026-0015', clientName: 'Seán Connolly', clientEmail: 'sean.connolly@finance.ie', appType: 'PRSA', submissionDate: '20/01/2026', lastActivity: '2026-02-05', lastActivityLabel: '28 days ago', status: 'APPLICATION_CREATED', hasNewActivity: false, documentsCount: 0 },
  { id: '9', caseRef: 'QRSL-2026-0012', clientName: 'Fiona Ryan', clientEmail: 'fiona.ryan@pharma.ie', appType: 'PRB', submissionDate: '15/01/2026', lastActivity: '2026-02-01', lastActivityLabel: '32 days ago', status: 'UNDER_REVIEW', hasNewActivity: false, documentsCount: 4 },
  { id: '10', caseRef: 'QRSL-2026-0008', clientName: 'Brian McCarthy', clientEmail: 'brian.mccarthy@agri.ie', appType: 'ARF', submissionDate: '08/01/2026', lastActivity: '2026-01-25', lastActivityLabel: '39 days ago', status: 'APPLICATION_SUBMITTED', hasNewActivity: false, documentsCount: 2 },
  { id: '11', caseRef: 'QRSL-2025-0198', clientName: 'Orla Gallagher', clientEmail: 'orla.gallagher@edu.ie', appType: 'PRSA', submissionDate: '20/12/2025', lastActivity: '2026-01-15', lastActivityLabel: '49 days ago', status: 'APPROVED', hasNewActivity: false, documentsCount: 5 },
  { id: '12', caseRef: 'QRSL-2025-0185', clientName: 'Tomás Burke', clientEmail: 'tomas.burke@engineering.ie', appType: 'ARF', submissionDate: '10/12/2025', lastActivity: '2026-01-05', lastActivityLabel: '59 days ago', status: 'APPROVED', hasNewActivity: false, documentsCount: 7 },
];

const MOCK_ACTIVITIES: ActivityItem[] = [
  { id: 'a1', action: 'Status changed to Under Review', caseRef: 'QRSL-2026-0041', time: '2 hours ago', icon: 'ArrowPathIcon', iconColor: 'bg-amber-500' },
  { id: 'a2', action: 'More information requested by QRSL admin', caseRef: 'QRSL-2026-0038', time: '1 day ago', icon: 'ExclamationCircleIcon', iconColor: 'bg-orange-500' },
  { id: 'a3', action: 'Document uploaded: Pension Statement.pdf', caseRef: 'QRSL-2026-0035', time: '2 days ago', icon: 'PaperClipIcon', iconColor: 'bg-blue-500' },
  { id: 'a4', action: 'Application approved by QRSL', caseRef: 'QRSL-2026-0031', time: '5 days ago', icon: 'CheckCircleIcon', iconColor: 'bg-green-500' },
  { id: 'a5', action: 'Comment added by admin', caseRef: 'QRSL-2026-0028', time: '8 days ago', icon: 'ChatBubbleLeftIcon', iconColor: 'bg-indigo-500' },
];

const MOCK_STATS: StatItem[] = [
  { label: 'Total Cases', value: 12, icon: 'DocumentTextIcon', iconColor: 'text-primary', bgColor: 'bg-primary/10', change: '+2 this month', changeType: 'up' },
  { label: 'Under Review', value: 2, icon: 'MagnifyingGlassIcon', iconColor: 'text-amber-600', bgColor: 'bg-amber-100', change: 'Awaiting QRSL', changeType: 'neutral' },
  { label: 'Approved', value: 4, icon: 'CheckCircleIcon', iconColor: 'text-green-600', bgColor: 'bg-green-100', change: '+1 this week', changeType: 'up' },
  { label: 'Action Required', value: 1, icon: 'ExclamationTriangleIcon', iconColor: 'text-orange-600', bgColor: 'bg-orange-100', change: 'Needs attention', changeType: 'down' },
];

const DEFAULT_FILTERS: FilterState = {
  search: '',
  status: '',
  appType: '',
  dateFrom: '',
  dateTo: '',
  sortField: 'submissionDate',
  sortDir: 'desc',
};

const BrokerDashboardInteractive = () => {
  const router = useRouter();
  const { user } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    setLastRefreshed('12:22');
  }, []);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      const now = new Date();
      setLastRefreshed(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    }, 800);
  }, []);

  const filteredAndSorted = useCallback(() => {
    let result = [...MOCK_CASES];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (c) =>
          c.clientName.toLowerCase().includes(q) ||
          c.caseRef.toLowerCase().includes(q) ||
          c.clientEmail.toLowerCase().includes(q)
      );
    }
    if (filters.status) {
      result = result.filter((c) => c.status === filters.status);
    }
    if (filters.appType) {
      result = result.filter((c) => c.appType === filters.appType);
    }
    if (filters.dateFrom) {
      result = result.filter((c) => {
        const [d, m, y] = c.submissionDate.split('/');
        return `${y}-${m}-${d}` >= filters.dateFrom;
      });
    }
    if (filters.dateTo) {
      result = result.filter((c) => {
        const [d, m, y] = c.submissionDate.split('/');
        return `${y}-${m}-${d}` <= filters.dateTo;
      });
    }

    result.sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';
      switch (filters.sortField) {
        case 'clientName': aVal = a.clientName; bVal = b.clientName; break;
        case 'appType': aVal = a.appType; bVal = b.appType; break;
        case 'status': aVal = a.status; bVal = b.status; break;
        case 'lastActivity': aVal = a.lastActivity; bVal = b.lastActivity; break;
        case 'caseRef': aVal = a.caseRef; bVal = b.caseRef; break;
        default: {
          const [ad, am, ay] = a.submissionDate.split('/');
          const [bd, bm, by] = b.submissionDate.split('/');
          aVal = `${ay}-${am}-${ad}`;
          bVal = `${by}-${bm}-${bd}`;
        }
      }
      const cmp = aVal.localeCompare(bVal);
      return filters.sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [filters]);

  const handleSortChange = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortField: field,
      sortDir: prev.sortField === field && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const cases = filteredAndSorted();

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-secondary">
          <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
          <span className="text-sm">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <RouteGuard requiredRole="broker">
      <div className="min-h-screen bg-background">
        <Header
          userRole="broker"
          userName={user?.name ?? 'James Mitchell'}
          brokerageName={user?.brokerageName ?? 'Pinnacle Pension Advisors'}
          notificationCount={2}
        />
        <RoleBasedNavigation
          userRole="broker"
          notificationCounts={{ cases: 2, admin: 0 }}
        />
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-heading font-700 text-text-primary">Broker Dashboard</h1>
                <p className="text-sm font-caption text-text-secondary mt-0.5">
                  Pinnacle Pension Advisors &mdash; Case Management Overview
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Compliance badge */}
                <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-success/10 border border-success/20">
                  <Icon name="ShieldCheckIcon" size={14} variant="solid" className="text-success" />
                  <span className="text-xs font-caption text-success font-medium">CBI Compliant</span>
                </div>
                {/* Refresh */}
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted rounded-md transition-standard border border-border"
                  title="Refresh data"
                >
                  <Icon name="ArrowPathIcon" size={15} variant="outline" className={isRefreshing ? 'animate-spin' : ''} />
                  <span className="hidden sm:inline text-xs font-caption">
                    {lastRefreshed ? `Updated ${lastRefreshed}` : 'Refresh'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 py-6 space-y-6">
          {/* Stats */}
          <DashboardStats stats={MOCK_STATS} />

          {/* Main content grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Cases table - takes 3/4 width on xl */}
            <div className="xl:col-span-3 space-y-4">
              {/* Filters */}
              <CaseTableFilters
                filters={filters}
                totalCount={MOCK_CASES.length}
                filteredCount={cases.length}
                onFilterChange={setFilters}
                onCreateNew={() => router.push('/case-creation-wizard')}
              />

              {/* Table */}
              <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
                <CaseTable
                  cases={cases}
                  sortConfig={{ field: filters.sortField, dir: filters.sortDir }}
                  onSortChange={handleSortChange}
                />

                {/* Table footer */}
                {cases.length > 0 && (
                  <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between">
                    <p className="text-xs font-caption text-text-secondary">
                      {cases.length} case{cases.length !== 1 ? 's' : ''} displayed
                    </p>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-caption text-text-secondary">Rows per page:</span>
                      <select className="text-xs font-caption border border-border rounded px-1.5 py-0.5 bg-input text-text-primary focus:outline-none">
                        <option>10</option>
                        <option>25</option>
                        <option>50</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - 1/4 width on xl */}
            <div className="xl:col-span-1 space-y-4">
              {/* Activity Feed */}
              <ActivityFeed activities={MOCK_ACTIVITIES} />

              {/* Quick Actions */}
              <div className="bg-card rounded-lg border border-border shadow-elevation-1">
                <div className="px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-heading font-600 text-text-primary">Quick Actions</h3>
                </div>
                <div className="p-3 space-y-2">
                  <button
                    onClick={() => router.push('/case-creation-wizard')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-standard"
                  >
                    <Icon name="PlusCircleIcon" size={17} variant="outline" />
                    <span>Create New Application</span>
                  </button>
                  <button
                    onClick={() => router.push('/case-detail-management')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md border border-border text-text-primary text-sm hover:bg-muted transition-standard"
                  >
                    <Icon name="DocumentTextIcon" size={17} variant="outline" />
                    <span>View All Cases</span>
                  </button>
                </div>
              </div>

              {/* Status Summary */}
              <div className="bg-card rounded-lg border border-border shadow-elevation-1">
                <div className="px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-heading font-600 text-text-primary">Status Breakdown</h3>
                </div>
                <div className="p-4 space-y-2.5">
                  {[
                    { label: 'Approved', count: 4, color: 'bg-green-500', pct: 33 },
                    { label: 'Under Review', count: 2, color: 'bg-amber-500', pct: 17 },
                    { label: 'Submitted', count: 2, color: 'bg-blue-500', pct: 17 },
                    { label: 'More Info Req.', count: 1, color: 'bg-orange-500', pct: 8 },
                    { label: 'Created', count: 1, color: 'bg-slate-400', pct: 8 },
                    { label: 'Declined', count: 1, color: 'bg-red-500', pct: 8 },
                    { label: 'Cancelled', count: 1, color: 'bg-gray-400', pct: 8 },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-caption text-text-secondary">{item.label}</span>
                        <span className="text-xs font-data text-text-primary font-medium">{item.count}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GDPR Notice */}
              <div className="bg-muted rounded-lg border border-border p-3">
                <div className="flex items-start gap-2">
                  <Icon name="LockClosedIcon" size={14} variant="solid" className="text-text-secondary mt-0.5 flex-shrink-0" />
                  <p className="text-xs font-caption text-text-secondary leading-relaxed">
                    All data is stored in EU-region servers and processed in compliance with GDPR. Data encrypted at rest with AES-256.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RouteGuard>
  );
};

export default BrokerDashboardInteractive;